import React from 'react';
import classNames from 'classnames';
import Linkify from 'linkify-react';
import { Link, useParams } from 'react-router';
import { FormattedMessage } from 'react-intl';
import { skipToken } from '@reduxjs/toolkit/query';

import { INACTIVITY_TIME_ON_FINAL_INSTRUCTIONS } from 'modules/auth/constants';

import useInactivity from 'hooks/useInactivity';
import { useDispatch, useSelector } from 'hooks';
import useLabelTranslation from 'hooks/useLabelTranslation';
import { useGetSelfServiceByIdQuery, useGetFinalInstructionsQuery } from 'modules/selfServices/service';
import { useSubmitFinalizationMutation } from 'modules/selfServices/finalizeService';
import { FINALIZE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';
import { isWrongStatusError } from 'types/Error';

import { logout } from 'modules/auth/actions';
import { finalizeSelfService } from 'modules/selfServices/actions';

import { isPublicDevice } from 'modules/dealers/selectors';
import { hasClickableInstruction } from 'modules/selfServices/selectors';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { getSelectedLanguage } from 'modules/translations/selectors';
import { DEFAULT_LANGUAGE, getLocaleUsage } from 'constants/LanguageConstants';
import type { PlatformEnum } from 'modules/dealers/types/FinalInstructionsResponse';
import type Language from 'types/LanguageEnum';

import Spinner from 'components/ui/Spinner';
import Icon from 'components/ui/Icon';
import Carousel from 'components/ui/Carousel';
import PageHeader from 'components/ui/PageHeader';
import Button, { ButtonType } from 'components/ui/Button';

import useFinalInstructionsButtons from './useFinalInstructionsButtons';

const getWalletImagePath = (type: PlatformEnum, language: Language) => (
  `${process.env.ASSETS_URL}/public/themes/default/images/wallets/${type.toLowerCase()}/${getLocaleUsage(language).toLowerCase()}_add_to_wallet.svg`
);

const FinalInstructions: React.FC = () => {
  const dispatch = useDispatch();
  const { getLabelTranslation } = useLabelTranslation();
  const { selfServiceId: selfServiceIdParams } = useParams();

  const isPublic = useSelector(isPublicDevice);
  const language = useSelector(getSelectedLanguage);
  const hasClickableDescription = useSelector(hasClickableInstruction);
  const selfServiceId = useSelector(getSelectedSelfServiceId) ?? selfServiceIdParams;

  const [isCarouselFullscreen, setIsCarouselFullscreen] = React.useState(false);

  const { data } = useGetSelfServiceByIdQuery(selfServiceId ? { id: selfServiceId } : skipToken);

  const [, { isLoading: isFinalizing, isError: isFinalizeError, error: finalizeError }] = useSubmitFinalizationMutation({
    fixedCacheKey: FINALIZE_FIXED_CACHE_KEY,
  });

  const hasFinalizationFailed = isFinalizeError && !isWrongStatusError(finalizeError);
  const shouldWaitFinalization = isFinalizing || hasFinalizationFailed;

  const { data: instructions, isLoading, isSuccess } = useGetFinalInstructionsQuery(
    shouldWaitFinalization ? skipToken : { selfServiceId },
  );

  const isFetching = isLoading || isFinalizing;
  const hasMedias = instructions?.medias?.length > 0;
  const { platforms } = instructions?.loginCodes ?? {};

  const handleCarouselOpen = React.useCallback(() => setIsCarouselFullscreen(true), []);
  const handleCarouselClose = React.useCallback(() => setIsCarouselFullscreen(false), []);

  const handleWalletImageError = React.useCallback(({ currentTarget }: React.SyntheticEvent<HTMLImageElement>) => {
    const { fallbackSrc } = currentTarget.dataset;
    if (fallbackSrc && currentTarget.getAttribute('src') !== fallbackSrc) {
      currentTarget.setAttribute('src', fallbackSrc);
    }
  }, []);

  const handleInactivity = React.useCallback(() => {
    if (isPublic) {
      dispatch(logout());
    }
  }, [dispatch, isPublic]);

  const handleRetryFinalization = React.useCallback(() => {
    dispatch(finalizeSelfService(0));
  }, [dispatch]);

  useInactivity({
    // Setting seconds to 0 will avoid a rerender per second when not needed
    seconds: isPublic ? INACTIVITY_TIME_ON_FINAL_INSTRUCTIONS : 0,
    onInactive: handleInactivity,
  });

  const buttons = useFinalInstructionsButtons({
    selfServiceId,
    instructions,
    data,
  });

  const finalInstructionMessage = React.useMemo(() => {
    if (isSuccess && instructions?.message) {
      return getLabelTranslation(instructions.message);
    }
    return undefined;
  }, [isSuccess, instructions?.message, getLabelTranslation]);

  const renderInstructionMessage = React.useMemo(() => {
    if (!finalInstructionMessage) {
      return null;
    }
    return (
      <p className="w-full shrink-0 text-low text-center text-s! leading-normal mt-0 mb-l whitespace-pre-wrap wrap-break-word">
        {hasClickableDescription ? (
          <Linkify options={{ target: { url: '_blank' } }}>
            {finalInstructionMessage}
          </Linkify>
        ) : (
          finalInstructionMessage
        )}
      </p>
    );
  }, [finalInstructionMessage, hasClickableDescription]);

  return (
    <>
      {isFetching && <Spinner className="homePage" />}
      {hasFinalizationFailed && (
        <div className="main-content flex flex-col items-center gap-l pt-22 kiosk:pt-50 kioskSharebox:pt-52">
          <p className="text-center text-default text-s! leading-normal">
            <FormattedMessage
              id="signature.error.description"
              defaultMessage="We could not send your signature. Please try again later or contact us."
            />
          </p>
          <Button onClick={handleRetryFinalization} testId="retryFinalization">
            <FormattedMessage id="retry" defaultMessage="Retry" />
          </Button>
          {isPublic && (
            <Button type={ButtonType.SECONDARY} onClick={handleInactivity} testId="goHome">
              <FormattedMessage id="quit" defaultMessage="Quit" />
            </Button>
          )}
        </div>
      )}
      {isSuccess && (
        <div className="flex flex-col items-center">
          <PageHeader
            hideSteps
            hidePrevButton
            hideLanguageSelect
            currentIndex={-1}
            length={0}
            title={(
              <FormattedMessage
                id="page.finalInstructions.title"
                defaultMessage="Thank you"
              />
            )}
          />
          <div
            className={classNames(
              'main-content pt-22 kiosk:pt-50 kioskSharebox:pt-52 kiosk:pb-48 kioskSharebox:pb-42 kioskSharebox:px-l',
              'kioskSharebox:grid kioskSharebox:h-svh kioskSharebox:max-w-none kioskSharebox:items-stretch kioskSharebox:overflow-hidden',
              'kioskSharebox:grid-rows-[minmax(0,max-content)] kioskSharebox:content-center-safe',
              hasMedias
                ? 'kioskSharebox:grid-cols-[1116fr_696fr] kioskSharebox:gap-x-l'
                : 'kioskSharebox:grid-cols-[984px] kioskSharebox:justify-center',
            )}
          >
            {hasMedias && (
              <div className="order-1 kioskSharebox:order-0 flex gap-4 mb-l flex-col rounded-2xl kiosk:rounded-3xl overflow-hidden w-full kioskSharebox:col-[1/2] kioskSharebox:self-start kioskSharebox:mb-0">
                <Carousel
                  compact
                  aspectRatioClassName="aspect-1028/559"
                  className="rounded-2xl kiosk:rounded-3xl"
                  medias={instructions.medias}
                  onClose={handleCarouselClose}
                  fullscreen={isCarouselFullscreen}
                  onMediaClick={handleCarouselOpen}
                />
              </div>
            )}
            <div
              data-testid="contentColumn"
              className="contents kioskSharebox:flex kioskSharebox:flex-col w-full kioskSharebox:col-[-2/-1] kioskSharebox:overflow-y-auto kioskSharebox:min-h-0"
            >
              {renderInstructionMessage}
              {buttons.length > 0 && (
                <div className="order-2 kioskSharebox:order-0 shrink-0 flex flex-col gap-2xs items-center p-s w-full rounded-3xl bg-card-bg">
                  {buttons.map(({
                    testId, label, addon, loading, disabled, icon, ...rest
                  }) => (
                    <React.Fragment key={testId}>
                      <Button
                        testId={testId}
                        type={ButtonType.WHITE}
                        disabled={disabled || loading}
                        className="flex items-center justify-between gap-xs kiosk:gap-s w-full py-3xs! pr-3xs! pl-5! kiosk:py-2xs! kiosk:pr-2xs! rounded-2xl! text-s! font-normal! leading-normal!"
                        {...rest}
                      >
                        {label}
                        <span
                          aria-hidden="true"
                          className="flex items-center justify-center shrink-0 size-10 kiosk:size-14 rounded-xl kiosk:rounded-2xl bg-(--color-neutral-50) text-default"
                        >
                          {loading
                            ? <Spinner className="size-6! fill-default!" />
                            : icon ?? <Icon className="fb-icon--arrow-right text-2xl" />}
                        </span>
                      </Button>
                      {addon}
                    </React.Fragment>
                  ))}
                  <div className="flex gap-3xs kiosk:gap-s items-center justify-center w-full">
                    {platforms?.map(({ type, path }) => (
                      <Link
                        key={type}
                        to={path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3xs"
                      >
                        <img
                          src={getWalletImagePath(type, language)}
                          alt={`${type} Wallet`}
                          className="h-12"
                          data-fallback-src={getWalletImagePath(type, DEFAULT_LANGUAGE)}
                          onError={handleWalletImageError}
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {isPublic && (
            <footer
              id="footer"
              className="nextButton kiosk:z-40 kioskSharebox:px-9! kioskSharebox:pb-6! before:h-0.5! kioskSharebox:before:inset-x-9!"
            >
              <Button
                onClick={handleInactivity}
                className="footer-button kioskSharebox:h-28!"
                testId="goHome"
              >
                <FormattedMessage id="quit" defaultMessage="Quit" />
              </Button>
            </footer>
          )}
        </div>
      )}
    </>
  );
};

export default FinalInstructions;
